<template>
  <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <button class="button-icon" title="Ajouter un achat" @click="$refs.purchaseModal.openModal()"><span class="fas fa-plus"></span></button>
      <button class="button-icon" title="Ajouter un avoir fournisseur" @click="$refs.purchaseModal.openModal(undefined, undefined, {documentType: PURCHASE_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value})"><span class="fas fa-rotate-left"></span></button>
      <button v-if="canImportIncomingInvoices" class="button-icon" title="Importer les factures reçues" @click="importIncomingInvoices"><span class="fas fa-file-import"></span></button>
    </template>
    <template v-slot:table>
      <thead v-if="$refs.table">
        <tr>
          <custom-header field="invoiceDate" class="col-fit">Date</custom-header>
          <custom-header field="supplierName" class="col-fit" :sortable="false">Fournisseur</custom-header>
          <custom-header field="reference" class="hide-for-mobile col-fit">Référence</custom-header>
          <custom-header field="label" class="col-fill">Libellé</custom-header>
          <custom-header field="category" class="hide-for-mobile col-fit">Catégorie</custom-header>
          <custom-header field="dueDate" class="hide-for-mobile col-fit">Échéance</custom-header>
          <custom-header field="ttcAmount" class="col-fit">Montant TTC</custom-header>
          <custom-header field="remainingAmount" class="hide-for-mobile col-fit" :sortable="false">Reste à payer</custom-header>
          <custom-header field="status" class="col-fit">Statut</custom-header>
          <custom-header field="actions" class="col-fit" :sortable="false">Actions</custom-header>
        </tr>
      </thead>
      <tbody v-if="data && data.length > 0">
        <tr v-for="purchase in $refs.table.$refs.searchArray.simpleFilter(data)" :key="purchase.id" class="business-row" @click="$refs.purchaseModal.openModal(purchase)">
          <custom-body field="invoiceDate" class="col-fit">{{ formatDate(purchase.invoiceDate) }}</custom-body>
          <custom-body field="supplierName" class="col-fit col-truncate" :title="purchase.supplierName">{{ purchase.supplierName }}</custom-body>
          <custom-body field="reference" class="hide-for-mobile col-fit col-truncate" :title="purchase.reference || '-'">{{ purchase.reference || '-' }}</custom-body>
          <custom-body field="label" class="col-fill" :title="purchase.label">
            <span v-if="isCreditNote(purchase)" class="icon" title="Avoir fournisseur"><span class="fas fa-rotate-left"></span></span>
            <span v-if="purchase.incomingInvoice" class="icon" :title="(isCreditNote(purchase) ? 'Avoir reçu' : 'Facture reçue') + (purchase.incomingInvoice.number ? ' n° ' + purchase.incomingInvoice.number : '')"><span class="fas fa-file-invoice"></span></span>
            <span v-if="purchase.allocations.length > 0" class="icon" :title="allocationsTitle(purchase)"><span class="fas fa-folder-open"></span></span>
            <span v-if="isAwaitingDelivery(purchase)" class="icon" :title="labelOf(DELIVERY_STATUS_ENUM, purchase.deliveryStatus)"><span class="fas fa-truck"></span></span>
            <span v-if="purchase.proofCount > 0" class="icon" :title="purchase.proofCount + ' justificatif(s)'"><span class="fas fa-paperclip"></span></span>
            {{ purchase.label }}
          </custom-body>
          <custom-body field="category" class="hide-for-mobile col-fit">{{ labelOf(PURCHASE_CATEGORY_ENUM, purchase.category) }}</custom-body>
          <custom-body field="dueDate" class="hide-for-mobile col-fit">
            <span :class="{overdue: purchase.isOverdue}" :title="purchase.isOverdue ? 'Échéance dépassée' : ''">{{ formatDate(purchase.dueDate) }}</span>
          </custom-body>
          <custom-body field="ttcAmount" class="col-fit amount">{{ formatSignedAmount(purchase, purchase.ttcAmount) }}</custom-body>
          <custom-body field="remainingAmount" class="hide-for-mobile col-fit amount">{{ formatSignedAmount(purchase, purchase.remainingAmount) }}</custom-body>
          <custom-body field="status" class="col-fit"><StatusBadge :status="purchase.status" :status-enum="statusEnumOf(purchase)" /></custom-body>
          <custom-body field="actions" class="col-fit">
            <button v-if="purchase.status === PURCHASE_STATUS_ENUM.TO_REVIEW.value" class="button-icon-custom" title="Valider l'achat" @click.stop="validate(purchase)"><span class="fas fa-circle-check"></span></button>
            <button v-if="canPay(purchase)" class="button-icon-custom" :title="isCreditNote(purchase) ? 'Enregistrer un remboursement' : 'Enregistrer un décaissement'" @click.stop="$refs.disbursementModal.openModal({purchase})"><span class="fas fa-money-bill-transfer"></span></button>
            <button v-if="!isCreditNote(purchase)" class="button-icon-custom" title="Créer un avoir sur cet achat" @click.stop="$refs.purchaseModal.openModal(undefined, undefined, {creditNoteOf: purchase})"><span class="fas fa-rotate-left"></span></button>
            <button v-if="purchase.incomingInvoice && purchase.incomingInvoice.providerInvoiceId" class="button-icon-custom" :title="isCreditNote(purchase) ? 'Télécharger l\'avoir' : 'Télécharger la facture'" @click.stop="downloadInvoice(purchase)"><span class="fas fa-download"></span></button>
            <button class="button-icon-custom" title="Supprimer" @click.stop="remove(purchase)"><span class="fas fa-trash-can"></span></button>
          </custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row"><td colspan="10" style="text-align: center">Aucun achat enregistré</td></tr></tbody>
    </template>
  </CustomTable>
  <PurchaseModal ref="purchaseModal" :callback=loadData />
  <DisbursementModal ref="disbursementModal" :callback=loadData />
  <PurchaseImportResultModal ref="importResultModal" />
  <ConfirmModal ref="deleteConfirmModal" title="Suppression" text="Supprimer cet achat ? Il ne sera plus possible de revenir en arrière ensuite." yes="Supprimer" no="Retour"/>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faDownload, faFileImport, faFileInvoice, faFolderOpen, faMoneyBillTransfer, faPaperclip, faPlus, faRotateLeft, faTrashCan, faTruck } from "@fortawesome/free-solid-svg-icons";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {downloadIncomingInvoice} from "@/purchase/services/PurchaseDocumentService";
import PurchaseModal from "@/purchase/components/modals/PurchaseModal.vue";
import DisbursementModal from "@/purchase/components/modals/DisbursementModal.vue";
import PurchaseImportResultModal from "@/purchase/components/modals/PurchaseImportResultModal.vue";
import {
  DELIVERY_STATUS_ENUM,
  PURCHASE_CATEGORY_ENUM,
  PURCHASE_DOCUMENT_TYPE_ENUM,
  PURCHASE_SOURCE_ENUM,
  PURCHASE_STATUS_ENUM,
  formatAmount,
  formatDate,
  formatSignedAmount,
  isCreditNote,
  labelOf,
  statusEnumOf
} from "@/purchase/utils";
library.add(faCircleCheck, faDownload, faFileImport, faFileInvoice, faFolderOpen, faMoneyBillTransfer, faPaperclip, faPlus, faRotateLeft, faTrashCan, faTruck);

export default {
  name: 'PurchaseTable',
  components: {PurchaseImportResultModal, DisbursementModal, PurchaseModal, ConfirmModal, StatusBadge, CustomBody, CustomHeader, CustomTable},
  data() {
    return {
      data: [],
      elements: [
        {label: "Date", value: "invoiceDate", type: "date"},
        {label: "Type", value: "documentType", type: "select", values: PURCHASE_DOCUMENT_TYPE_ENUM},
        {label: "Référence", value: "reference", type: "text"},
        {label: "Libellé", value: "label", type: "text"},
        {label: "Catégorie", value: "category", type: "select", values: PURCHASE_CATEGORY_ENUM},
        {label: "Échéance", value: "dueDate", type: "date"},
        {label: "Montant HT", value: "htAmount", type: "number"},
        {label: "Montant TTC", value: "ttcAmount", type: "number"},
        {label: "Payé", value: "paidAmount", type: "number"},
        {label: "Statut", value: "status", type: "select", values: PURCHASE_STATUS_ENUM},
        {label: "Livraison", value: "deliveryStatus", type: "select", values: DELIVERY_STATUS_ENUM},
        {label: "Origine", value: "source", type: "select", values: PURCHASE_SOURCE_ENUM},
        {label: "Actions", value: undefined, type: undefined},
      ],
      searchOptions: {
        filters: [],
        sorts: [{field: "invoiceDate", order: SORT_OPERATOR_ENUM.DESCENDING.value}]
      },
      // Les factures reçues viennent de l'option facturation électronique
      canImportIncomingInvoices: !!(localStorage.options && localStorage.options.includes("invoice_transmission")),
      PURCHASE_CATEGORY_ENUM,
      PURCHASE_DOCUMENT_TYPE_ENUM,
      PURCHASE_STATUS_ENUM,
      DELIVERY_STATUS_ENUM
    };
  },
  async mounted() {
    // Lien depuis un autre écran : /purchases?filters=...
    this.$refs.table.$refs.searchArray.initWithURLParams();
    await this.loadData();
  },
  methods: {
    formatAmount,
    formatDate,
    formatSignedAmount,
    isCreditNote,
    labelOf,
    statusEnumOf,
    // Biens commandés pas encore entièrement reçus
    isAwaitingDelivery(purchase) {
      return !!purchase.deliveryStatus && purchase.deliveryStatus !== DELIVERY_STATUS_ENUM.RECEIVED.value;
    },
    async loadData() {
      this.$refs.table?.startLoading();
      this.data = [];
      const resp = await PurchaseController.getTable(this.$refs.table.buildURLParams());
      if (!resp.errors) {
        this.data.push(...resp);
      }
      this.$refs.table?.stopLoading();
    },
    // Un achat peut être payé une fois validé, tant qu'il reste quelque chose à payer
    canPay(purchase) {
      return purchase.status !== PURCHASE_STATUS_ENUM.TO_REVIEW.value && purchase.remainingAmount > 0;
    },
    // Une ligne par dossier (la somme de ses parts si l'achat est ventilé par article)
    allocationsTitle(purchase) {
      const orders = new Map();
      purchase.allocations.forEach(allocation => {
        const key = String(allocation.orderId);
        const order = orders.get(key) ?? {label: `N°${allocation.orderNumber ?? '?'}${allocation.orderTitle ? ' - ' + allocation.orderTitle : ''}`, htAmount: 0};
        order.htAmount += Number(allocation.htAmount) || 0;
        orders.set(key, order);
      });
      return [...orders.values()].map(order => `${order.label} : ${formatAmount(order.htAmount)} HT`).join('\n');
    },
    async validate(purchase) {
      this.$refs.table?.startLoading();
      const res = await PurchaseController.validate(purchase.id);
      this.$refs.table?.stopLoading();
      if (!res.errors) {
        (new NotificationService).addNotification("success", isCreditNote(purchase) ? "Avoir validé" : "Achat validé");
        await this.loadData();
      }
    },
    downloadInvoice(purchase) {
      return downloadIncomingInvoice(purchase);
    },
    async remove(purchase) {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => PurchaseController.delete(purchase.id));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", isCreditNote(purchase) ? "Avoir supprimé" : "Achat supprimé");
        await this.loadData();
      }
    },
    async importIncomingInvoices() {
      this.$refs.table?.startLoading();
      const summary = await PurchaseController.importIncomingInvoices();
      this.$refs.table?.stopLoading();
      if (summary && !summary.errors) {
        await this.loadData();
        this.$refs.importResultModal.openModal(summary);
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';

.button-icon
  margin-right 10px

.icon
  margin-right 6px
  color #6b7280

.amount
  text-align right
  font-variant-numeric tabular-nums

.overdue
  color #dc2626
  font-weight 600
</style>
